import * as React from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import { RegisterFormContext } from './RegisterForm.context';
import { FormWrapper, View } from './RegisterForm.style';
import { InputMaskStyled } from '../../elements/forms/InputContainer';
import { EnderecoDoCep, lookupZipcode } from '../../utils/handleZipcode.util';
import { RegisterFormProps } from './Register.schema';
import { RegisterMap } from './RegisterMap';
import { QUERY_GEOCODE_ADDRESS } from './RegisterForm.graphql';
import { client } from '../../../core/stores';

const Resumo = styled.div`
  background: #f7f9fc;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 12px;
  padding: 14px 16px;

  strong {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
    text-transform: uppercase;
    color: #5b6b7c;
  }
`;

const Voltar = styled.a`
  color: #006bf3;
  display: inline-block;
  margin-top: 16px;
`;

export function RegisterFormStep3() {
  const {
    step,
    onHandleSubmit,
    isSubmitting,
    control,
    errors,
    setValue,
    watch,
    goBack,
  } = React.useContext(RegisterFormContext);

  const apollo = React.useMemo(() => client('global'), []);
  const [buscando, setBuscando] = React.useState(false);
  const [cepInvalido, setCepInvalido] = React.useState(false);

  const zipcode = watch('zipcode');
  const street = watch('street');
  const neighborhood = watch('neighborhood');
  const city = watch('city');
  const state = watch('state');
  const latitude = watch('latitude');
  const longitude = watch('longitude');

  React.useEffect(() => {
    if (step === 3) window.scrollTo(0, 0);
  }, [step]);

  const aplicarEndereco = React.useCallback(
    (endereco: EnderecoDoCep) => {
      setValue('street', endereco.street, {shouldValidate: true});
      setValue('neighborhood', endereco.neighborhood, {shouldValidate: true});
      setValue('city', endereco.city, {shouldValidate: true});
      setValue('state', endereco.state as RegisterFormProps['state'], {shouldValidate: true});
    },
    [setValue],
  );

  const digitos = (zipcode ?? '').replace(/\D/g, '');

  // Duas chamadas em sequencia: o ViaCEP resolve o endereco, o Rails resolve a
  // coordenada. Trocar de CEP rapido cancela a busca anterior — sem isso a
  // resposta antiga chegava depois e sobrescrevia o endereco novo.
  React.useEffect(() => {
    if (digitos.length !== 8) {
      setCepInvalido(false);
      return undefined;
    }

    const controller = new AbortController();
    let vivo = true;

    const buscar = async () => {
      setBuscando(true);
      setCepInvalido(false);

      try {
        const endereco = await lookupZipcode(digitos, {signal: controller.signal});
        if (!vivo) return;

        if (!endereco) {
          setCepInvalido(true);
          setValue('latitude', null);
          setValue('longitude', null);
          return;
        }

        aplicarEndereco(endereco);

        const {data} = await apollo.query({
          query: QUERY_GEOCODE_ADDRESS,
          variables: {
            zipcode: digitos,
            street: endereco.street,
            city: endereco.city,
            state: endereco.state,
          },
          fetchPolicy: 'network-only',
        });

        if (!vivo) return;

        // Sem coordenada o cadastro segue: o mapa avisa que esta indisponivel
        // e a filial fica sem lat/long, em vez de receber um ponto inventado.
        setValue('latitude', data?.geocodeAddress?.latitude ?? null);
        setValue('longitude', data?.geocodeAddress?.longitude ?? null);
      } catch (falha) {
        if ((falha as Error).name !== 'AbortError' && vivo) setCepInvalido(true);
      } finally {
        if (vivo) setBuscando(false);
      }
    };

    buscar();

    return () => {
      vivo = false;
      controller.abort();
    };
  }, [aplicarEndereco, apollo, digitos, setValue]);

  if (step !== 3) return null;

  const enderecoResolvido = [street, neighborhood].filter(Boolean).join(' — ');
  const cidadeResolvida = [city, state].filter(Boolean).join(' / ');

  const mensagemDoMapa = () => {
    if (buscando) return 'Procurando o endereço…';
    if (cepInvalido) return 'CEP não encontrado.';
    if (digitos.length === 8) return 'Não conseguimos posicionar o mapa, mas isso não impede seu cadastro.';

    return 'Digite o CEP para ver o endereço no mapa.';
  };

  return (
    <View>
      <h1>Só falta confirmar quem é você</h1>

      <FormWrapper onSubmit={onHandleSubmit} style={{flexDirection: 'column'}}>
        <Controller
          name="cpf"
          control={control}
          render={({field}) => (
            <InputMaskStyled
              {...field}
              placeholder="CPF"
              mask="999.999.999-99"
              label="CPF"
              type="tel"
              s={12}
              className={errors.cpf?.message ? 'invalid' : ''}
              error={errors.cpf?.message}
            />
          )}
        />

        <Controller
          name="zipcode"
          control={control}
          render={({field}) => (
            <InputMaskStyled
              {...field}
              placeholder="CEP"
              label="CEP"
              type="tel"
              mask="99999-999"
              s={12}
              className={errors.zipcode?.message || cepInvalido ? 'invalid' : ''}
              error={
                cepInvalido ? 'Não encontramos esse CEP. Confira os números.' : errors.zipcode?.message
              }
            />
          )}
        />

        {/* O endereco nao e editavel: veio do CEP e serve de confirmacao. */}
        {(enderecoResolvido || cidadeResolvida) && (
          <Resumo>
            <strong>Endereço</strong>
            {enderecoResolvido && <div>{enderecoResolvido}</div>}
            {cidadeResolvida && <div>{cidadeResolvida}</div>}
          </Resumo>
        )}

        <RegisterMap latitude={latitude} longitude={longitude} mensagemVazia={mensagemDoMapa()} />

        <button
          className="btn btn-primary"
          type="button"
          onClick={onHandleSubmit}
          disabled={isSubmitting || buscando}>
          {isSubmitting ? 'Criando sua conta...' : 'Criar minha conta'}
        </button>

        <Voltar
          href="#"
          onClick={(evento) => {
            evento.preventDefault();
            goBack();
          }}>
          Voltar
        </Voltar>
      </FormWrapper>
    </View>
  );
}
